import Avatar from '@shared/components/Avatar/Avatar';
import Checkbox from '@shared/components/Checkbox/Checkbox';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CenteredSpinner from 'components/CenteredSpinner/CenteredSpinner';
import NoResultsPlaceholder from 'components/NoResultsPlaceholder/NoResultsPlaceholder';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { getInviteableWorkspaceMembers } from 'modules/Workspaces/WorkspaceSettingsModal/MembersTab/MembersSection/utils';
import useWorkspaceMembers from 'modules/Workspaces/WorkspaceSettingsModal/MembersTab/useWorkspaceMembers';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { getIsFromGuestServer } from 'modules/Workspaces/WorkspacesProvider/utils';

import { memberMatchesInviteSearch } from './memberMatchesInviteSearch';

interface Candidate {
  userId: string;
  displayName: string;
}

interface OrganisationMembersListProps {
  searchTerm: string;
  participants: string[];
  onToggle: (mxid: string, displayName?: string) => void;
  excludeUserIds?: ReadonlySet<string>;
}

const OrganisationMembersListInner = ({
  searchTerm,
  participants,
  onToggle,
  excludeUserIds,
}: OrganisationMembersListProps) => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const workspaceMembers = useWorkspaceMembers();
  const myUserId = matrixClient.getUserId();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Recompute the member list every time it is shown / whenever workspace membership changes, with a
  // loading state while the latest membership is gathered. Selection (participants) and search are
  // applied cheaply at render so toggling a checkbox never re-triggers the spinner.
  useEffect(() => {
    setIsLoading(true);
    const handle = window.setTimeout(() => {
      const next = getInviteableWorkspaceMembers({
        members: workspaceMembers,
        myUserId,
        excludeUserIds,
        isGuestServerMember: getIsFromGuestServer,
      }).map((m) => ({ userId: m.id, displayName: m.name || m.id }));
      setCandidates(next);
      setIsLoading(false);
    }, 0);

    return () => window.clearTimeout(handle);
  }, [workspaceMembers, myUserId, excludeUserIds]);

  // #8071 — keep checked members visible in the list (no `notSelected` pre-filter), mirroring
  // SuggestedMembersList. Selection is reflected by the checkbox; deselection happens in place.
  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return candidates;
    return candidates.filter((c) => memberMatchesInviteSearch(c.displayName, c.userId, searchTerm));
  }, [candidates, searchTerm]);

  if (isLoading) {
    return <CenteredSpinner className="!mb-0 py-4" />;
  }

  if (filtered.length === 0) {
    return <NoResultsPlaceholder className="bg-white !mb-0 !p-2" title={t('workspaces.no_results')} />;
  }

  return (
    <div className="flex min-w-0 flex-col">
      {filtered.map((c) => (
        <div key={c.userId} className="flex items-center justify-between py-1">
          <div className="flex min-w-0 items-center gap-4">
            <Avatar size="xs" name={c.displayName} />
            <span className="min-w-0 truncate text-size-sm font-medium">{c.displayName}</span>
          </div>
          <Checkbox checked={participants.includes(c.userId)} onChange={() => onToggle(c.userId, c.displayName)} />
        </div>
      ))}
    </div>
  );
};

const OrganisationMembersList = (props: OrganisationMembersListProps) => {
  const { t } = useTranslation();
  const { workspaceId } = useWorkspacesContext();

  if (!workspaceId) {
    return <NoResultsPlaceholder className="bg-white !mb-0 !p-2" title={t('workspaces.no_results')} />;
  }

  return <OrganisationMembersListInner {...props} />;
};

export default OrganisationMembersList;
